import { createToast } from "../components/Toast";
import { ENV, PUBLIC_URL } from "./const";

export interface ApiError {
    message: string
}

export enum MessageKind {
    Info = 1,
    Error = 2,
    Success = 3
}

export function setMessage(msg: string, kind: MessageKind){
    if(kind === MessageKind.Info){
        sessionStorage.setItem("info-msg", msg);
    }
    else if(kind === MessageKind.Error){
        sessionStorage.setItem("error-msg", msg);
    }
    else if(kind === MessageKind.Success){
        sessionStorage.setItem("success-msg", msg);
    }
}

export function consumeMessages(){
    const err = sessionStorage.getItem("error-msg");
    if(err){
        createToast(err, MessageKind.Error);
        sessionStorage.removeItem("error-msg")
    }

    const info = sessionStorage.getItem("info-msg");
    if(info){
        createToast(info, MessageKind.Info);
        sessionStorage.removeItem("info-msg")
    }

    const success = sessionStorage.getItem("success-msg");
    if(success){
        createToast(success, MessageKind.Success);
        sessionStorage.removeItem("success-msg")
    }
}

export function refreshPage(){
    window.location.reload()
}

export function refactorCalendarName(name: string){
    return name.replace("@group.v.calendar.google.com", "")
}

export function sleep(ms: number) {
    return new Promise( resolve => setTimeout(resolve, ms) );
}

export const SUPPORTED_LANGUAGES = ["en", "fr", "it", "es"];

export function languageAwareUrl(url: string){
    const i18lgn = sessionStorage.getItem("i18nextLng");
    let prefixlgn = window.location.pathname.slice(1,3);
    if(ENV === "development"){
        prefixlgn = window.location.pathname.slice(5,7);
    }
    let result = url;
    if(SUPPORTED_LANGUAGES.includes(prefixlgn)){
        result = `${PUBLIC_URL}/${prefixlgn}${url}`;
    } else if(i18lgn !== null && SUPPORTED_LANGUAGES.includes(i18lgn)){
        result = `${PUBLIC_URL}/${i18lgn}${url}`;
    } else {
        result = `${PUBLIC_URL}${result}`;
    }
    return result;
}

export const withLoading = (setIsLoading: (loading: boolean) => void) => {
    return function <T extends any[], R>(fn: (...args: T) => R | Promise<R>) {
        return (...args: T): R | Promise<R> => {
            setIsLoading(true);
            try {
                const result = fn(...args);
                if (result instanceof Promise) {
                    return result.finally(() => setIsLoading(false));
                }
                setIsLoading(false);
                return result;
            } catch (error) {
                setIsLoading(false);
                throw error;
            }
        };
    };
};

export enum CalendarProvider {
    GOOGLE = "google",
    OUTLOOK = "outlook",
    ICS = "ics",
    APPLE = "apple"
}

/** FirstPromoter referral query keys that must survive CTA redirects before fpr.js sets cookies. */
const FIRSTPROMOTER_QUERY_PARAMS = new Set(['fpr', 'via', 'fp_ref']);

export function computeURLParameters(parameters: Record<string, string>, include_utm: boolean = true): Record<string, string> {
    let params: Record<string, string> = {}
    // Append the provided parameters to the URL
    Object.entries(parameters).forEach(([key, value]) => {
        params[key] = value;
    });

    // Optionally include attribution parameters from the current URL
    if (include_utm) {
        const currentUrlParams = new URLSearchParams(window.location.search);
        currentUrlParams.forEach((value, key) => {
            if (key.startsWith('utm_') || FIRSTPROMOTER_QUERY_PARAMS.has(key)) {
                params[key] = value;
            }
        });
    }

    return params;
}

export function buildUrlWithParameters(url: string, parameters: Record<string, string>, include_utm: boolean = true) {
    const newUrl = new URL(url, window.location.origin);
    const params = computeURLParameters(parameters, include_utm);
    Object.entries(params).forEach(([key, value]) => {
        newUrl.searchParams.append(key, value);
    });
    return newUrl.toString();
}

export function redirectWithParameters(url: string, parameters: Record<string, string>, include_utm: boolean = true) {
    // Create a URL object from the provided URL
    const newUrl = buildUrlWithParameters(url, parameters, include_utm);
    // Redirect to the new URL
    window.location.href = newUrl;
}

export function convertToAdminConsentUrl(oauthUrl: string): string {
    const url = new URL(oauthUrl);
    
    // Replace /common/oauth2/v2.0/authorize with /{tenant-id}/v2.0/adminconsent
    url.pathname = `/organizations/v2.0/adminconsent`;
    
    // Get the redirect_uri parameter
    url.searchParams.delete('response_type');
    url.searchParams.delete('prompt');
    url.searchParams.set('state', 'outlook-admin-consent');
    
    return url.toString();
}